'use client';

import { FC, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { useTeamContext } from '../../providers/TeamContext';

import { Player, Week, Match, PredictionMatchResult } from '@repo/types'; 

import { DriverCard } from "./driver-card"
import { LoadingSpinner } from "../../components/loading-spinner";
import TeamStats from '../../components/team-stats';

const moment = require('moment');
require('moment/locale/nl');
moment.locale('nl');

interface PointsOverviewProps {
  points: number
  rankingGP: number
  totalPoints: number
  rankingTotal: number
}

export function PointsOverview({ points, rankingGP, totalPoints, rankingTotal }: PointsOverviewProps) {
  return (
    <div className="relative rounded-lg overflow-hidden ">
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="stripe-pattern"
            patternUnits="userSpaceOnUse"
            width="20"
            height="20"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="20"
              stroke="white"
              strokeWidth="20"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#stripe-pattern)" />
      </svg>
      <div className="rounded-xl p-4 shadow-sm">
        <div className="grid grid-cols-2 gap-4">
          {[
            { value: points, label: 'Punten' },
            { value: rankingGP.toLocaleString('nl-NL'), label: 'Ranking GP' },
            { value: totalPoints, label: 'Punten Totaal' },
            { value: rankingTotal.toLocaleString('nl-NL'), label: 'Ranking Totaal' },
          ].map(({ value, label }, index) => (
            <div
              key={index}
              className="bg-white relative rounded-lg overflow-hidden p-1 flex flex-col items-center justify-center"
            >            
              <span className="relative z-10 text-[#1f2544] text-2xl font-extra">
                {value}
              </span>
              <span className="relative z-10 text-[#1f2544] text-sm mt-1">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Empty state component for predictions
// function EmptyPredictionState() {
//   return (
//     <div className="bg-white rounded-b-lg p-8 text-center">
//       <div className="text-gray-500">
//         <p className="text-lg font-medium mb-2">Resultaten nog niet beschikbaar</p>
//         <p>De resultaten worden bijgewerkt zodra de race is afgelopen.</p>
//       </div>
//     </div>
//   );
// }

// Max position component with empty state handling
function MaxPositionDisplay({ brand, title, colorClass, actualPosition, predictedPosition, points, selectionBlockIndex, selectionBlockSize }: any) {
  return (
    <div className="md:col-span-1">
      <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 ${colorClass}`}>
      <div className="text-sm">{selectionBlockIndex} van de {selectionBlockSize}</div>
        <h2 className="font-extra">Max Verstappen in de <span className={colorClass}>{title}</span></h2>
      </div>

      <div className="bg-white rounded-b-lg pt-2">
        
          <div className="flex justify-between items-center">
            <span className="px-4">{predictedPosition}e</span>
            {actualPosition !== undefined ? (<><span className="font-extra px-4">{actualPosition}e</span><span className="font-extra px-4">+{points}</span></>):  
          (
          <div className="p-4 text-center text-gray-500">
            <p>Resultaten nog niet beschikbaar</p>
          </div>
          )
        }
            
          </div>
         
      </div>
    </div>
  );
}

interface PointsProps {
  weekId: number;
}

const Points: FC<PointsProps> = ({ weekId }) => {
  const { brand, competitionData } = useCompetitionContext();
  const navigate = useNavigate();
  const params = useParams();
  const { team } = useTeamContext();

  // console.log('points page', team, competitionData);

  // if (weekId >= competitionData.deadlineInfo.deadlineWeek) {
  //   console.log('leave points');
  //   navigate(`/fantasy/edit/${params.id}`);
  // }

  const weekStats = team.stats?.find((stat :any) => stat.weekId === weekId);
  const currentMatchType = competitionData.matches.find((match: Match) => match.weekId === weekId)?.type;

  const drivers: Player[] = competitionData.players || [];
  const driverStats = competitionData.stats.filter((stat: any) => stat.w === weekId);
  const hasResults = driverStats && driverStats.length > 0;
  
  // Find Max's stats if results are available
  const MaxStats = hasResults ? driverStats.find((stat: any) => stat.i === 93875) : null;

  const selectionBlockSize = currentMatchType === 'sprint' ? 9 : 5;

  // Qualifying data
  const qualifyingResult = MaxStats && JSON.parse(MaxStats.r).qualifying_position;
  const qualifyingPoints = qualifyingResult !== undefined ? 
    (Math.abs(parseInt(team?.extra?.qualifyingPosition) - qualifyingResult) === 0 ? 5 : 
     Math.abs(parseInt(team?.extra?.qualifyingPosition) - qualifyingResult) === 1 ? 2 : 0) : 0;

  // Race data
  const raceResult = MaxStats && JSON.parse(MaxStats.r).race_position;
  const racePoints = raceResult !== undefined ? 
    (Math.abs(parseInt(team?.extra?.racePosition) - raceResult) === 0 ? 10 : 
     Math.abs(parseInt(team?.extra?.racePosition) - raceResult) === 1 ? 5 : 0) : 0;

  const qualifyingSprintResult = MaxStats && JSON.parse(MaxStats.r).sprint_qualifying_position;
  const qualifyingSprintPoints = qualifyingSprintResult !== undefined ? 
    (Math.abs(parseInt(team?.extra?.qualifyingSprintPosition) - qualifyingSprintResult) === 0 ? 2 : 
    Math.abs(parseInt(team?.extra?.qualifyingSprintPosition) - qualifyingSprintResult) === 1 ? 1 : 0) : 0;

  // Sprint data
  const sprintResult = MaxStats && JSON.parse(MaxStats.r).sprint_position;
  const sprintPoints = sprintResult !== undefined ? 
    (Math.abs(parseInt(team?.extra?.sprintPosition) - sprintResult) === 0 ? 4 : 
     Math.abs(parseInt(team?.extra?.sprintPosition) - sprintResult) === 1 ? 2 : 0) : 0;

  // Process qualifying predictions with empty state handling
  const qualifyingPrediction: (PredictionMatchResult & { driver: Player })[] = 
    (team?.prediction?.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
      if (currentPrediction.stage === 'qualifying') {
        const driver = drivers.find(driver => driver.id === currentPrediction.id);
        if (driver) {
          if (hasResults) {
            const driverStat = driverStats.find((stat: any) => stat.i === driver.id)?.r
            const result = JSON.parse(driverStat);
            const currentPredictioinPosition = currentPrediction.index !== undefined && currentPrediction.index+1
            if (result) {
              const driverWithStats = {
                ...driver,
                stats: [{
                  points: currentPredictioinPosition === result.qualifying_position ? 5 
                    : result.qualifying_position <= 3 ? 2 
                    : 0,
                  results: result.qualifying_position,
                }]
              };
              accumulator.push({ ...currentPrediction, driver: driverWithStats });
            } else {
              // No stats for this driver
              accumulator.push({ ...currentPrediction, driver });
            }
          } else {
            // No results available yet
            accumulator.push({ ...currentPrediction, driver });
          }
        }
      }
      return accumulator;
    }, []) || [])
    .sort((a, b) => {
      // Sort by index, handling undefined values
      if (a.index === undefined) return 1;  // Push undefined values to the end
      if (b.index === undefined) return -1; // Push undefined values to the end
      return a.index - b.index;            // Sort by index in ascending order
    });

  // Process race predictions with empty state handling
  const racePrediction: (PredictionMatchResult & { driver: Player })[] = 
    (team?.prediction?.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
      if (currentPrediction.stage === 'race') {
        const driver = drivers.find(driver => driver.id === currentPrediction.id);
        if (driver) {
          if (hasResults) {
            const driverStat = driverStats.find((stat: any) => stat.i === driver.id)?.r
            const result = JSON.parse(driverStat);
            const currentPredictioinPosition = currentPrediction.index !== undefined && currentPrediction.index+1

            if (result) {
              const driverWithStats = {
                ...driver,
                stats: [{
                  points: currentPredictioinPosition === result.race_position ? 10 
                    : result.race_position <= 3 ? 5 
                    : 0,
                  results: result.race_position,
                }]
              };
              accumulator.push({ ...currentPrediction, driver: driverWithStats });
            } else {
              // No stats for this driver
              accumulator.push({ ...currentPrediction, driver });
            }
          } else {
            // No results available yet
            accumulator.push({ ...currentPrediction, driver });
          }
        }
      }
      return accumulator;
    }, []) || [])
    .sort((a, b) => {
      // Sort by index, handling undefined values
      if (a.index === undefined) return 1;  // Push undefined values to the end
      if (b.index === undefined) return -1; // Push undefined values to the end
      return a.index - b.index;            // Sort by index in ascending order
    });

  const qualifyingSprintPrediction: (PredictionMatchResult & { driver: Player })[] = 
      (team?.prediction?.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
        if (currentPrediction.stage === 'qualifyingsprint') {
          const driver = drivers.find(driver => driver.id === currentPrediction.id);
          if (driver) {
            if (hasResults) {
              const driverStat = driverStats.find((stat: any) => stat.i === driver.id)?.r
              const result = JSON.parse(driverStat);
              const currentPredictioinPosition = currentPrediction.index !== undefined && currentPrediction.index+1
              
              if (result) {
                const driverWithStats = {
                  ...driver,
                  stats: [{
                    points: currentPredictioinPosition === result.sprint_qualifying_position ? 2 
                      : result.sprint_qualifying_position <= 3 ? 1 
                      : 0,
                    results: result.sprint_qualifying_position,
                  }]
                };
                accumulator.push({ ...currentPrediction, driver: driverWithStats });
              } else {
                // No stats for this driver
                accumulator.push({ ...currentPrediction, driver });
              }
            } else {
              // No results available yet
              accumulator.push({ ...currentPrediction, driver });
            }
          }
        }
        return accumulator;
      }, []) || [])
      .sort((a, b) => {
        // Sort by index, handling undefined values
        if (a.index === undefined) return 1;  // Push undefined values to the end
        if (b.index === undefined) return -1; // Push undefined values to the end
        return a.index - b.index;            // Sort by index in ascending order
      });

  // Process sprint predictions with empty state handling
  const sprintPrediction: (PredictionMatchResult & { driver: Player })[] = 
    (team?.prediction?.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
      if (currentPrediction.stage === 'sprint') {
        const driver = drivers.find(driver => driver.id === currentPrediction.id);
        if (driver) {
          if (hasResults) {
            const driverStat = driverStats.find((stat: any) => stat.i === driver.id)?.r
            const result = JSON.parse(driverStat);
            const currentPredictioinPosition = currentPrediction.index !== undefined && currentPrediction.index+1
            
            if (result) {
              const driverWithStats = {
                ...driver,
                stats: [{
                  points: currentPredictioinPosition === result.sprint_position ? 4 
                    : result.sprint_position <= 3 ? 2 
                    : 0,
                  results: result.sprint_position,
                }]
              };
              accumulator.push({ ...currentPrediction, driver: driverWithStats });
            } else {
              // No stats for this driver
              accumulator.push({ ...currentPrediction, driver });
            }
          } else {
            // No results available yet
            accumulator.push({ ...currentPrediction, driver });
          }
        }
      }
      return accumulator;
    }, []) || [])
    .sort((a, b) => {
      // Sort by index, handling undefined values
      if (a.index === undefined) return 1;  // Push undefined values to the end
      if (b.index === undefined) return -1; // Push undefined values to the end
      return a.index - b.index;            // Sort by index in ascending order
    });

  // Get race name - assuming we have this information in competitionData
  const raceName = competitionData.matches.find((match: Match) => match.weekId === weekId)?.feedUrl || 'Grand Prix';

  return (
    <>
      <div className="bg-gpspel-page p-4 space-y-6">

        <PointsOverview 
          points={weekStats?.points || 0}
          rankingGP={weekStats?.rank || 0}
          totalPoints={team?.stats?.reduce((total: number, stat:any) => total + stat.points, 0) || 0}
          rankingTotal={team?.rank || 0}
        />

        <div className="">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg`}>
            <div className="text-sm">1 van de {selectionBlockSize}</div>
            <h1 className="font-extra">
              Jouw team voor de GP van {raceName}
            </h1>
          </div>
          <div className="bg-white rounded-b-lg pt-2">
            <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
              {team?.starting?.map((driver, index) => (
                <DriverCard
                  key={`selected-${driver.id}`}
                  driver={driver}
                  weekId={weekId}
                  type='fantasyPick'
                />
              ))}
            </div>
          </div>
        </div>

        {currentMatchType === 'sprint' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-pink-400`}>
              <div className="text-sm">2 van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Jouw podium voor de <span className='text-pink-400'>sprintkwalificatie</span>
              </h1>
            </div>
            {/* {hasResults ? ( */}
              <div className="bg-white rounded-b-lg pt-2">
                <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                  {qualifyingSprintPrediction.map((prediction: any, index) => (
                    <DriverCard
                      key={`sprint-selected-${prediction.driver.id}`}
                      driver={prediction.driver}
                      type='prediction'
                      weekId={weekId}
                      cardIndex={index}
                    />
                  ))}
                </div>
              </div>
            {/* ) : (
              <EmptyPredictionState />
            )} */}
          </div>

          <MaxPositionDisplay 
            brand={brand}
            title="sprintkwalificatie"
            colorClass="border-b-pink-400 text-pink-400"
            actualPosition={qualifyingSprintResult ? qualifyingSprintResult : undefined}
            predictedPosition={team?.extra?.qualifyingSprintPosition}
            points={qualifyingSprintPoints}
            selectionBlockIndex={3}
            selectionBlockSize={selectionBlockSize}
          />
        </div>
        )}

        {currentMatchType === 'sprint' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-pink-400`}>
              <div className="text-sm">4 van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Jouw podium voor de <span className='text-pink-400'>sprintrace</span>
              </h1>
            </div>
            {/* {hasResults ? ( */}
              <div className="bg-white rounded-b-lg pt-2">
                <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                  {sprintPrediction.map((prediction: any, index) => (
                    <DriverCard
                      key={`sprint-selected-${prediction.driver.id}`}
                      driver={prediction.driver}
                      type='prediction'
                      weekId={weekId}
                      cardIndex={index}
                    />
                  ))}
                </div>
              </div>
            {/* ) : (
              <EmptyPredictionState />
            )} */}
          </div>

          <MaxPositionDisplay 
            brand={brand}
            title="sprint"
            colorClass="border-b-pink-400 text-pink-400"
            actualPosition={sprintResult ? sprintResult : undefined}
            predictedPosition={team?.extra?.sprintPosition}
            points={sprintPoints}
            selectionBlockIndex={5}
          />
        </div>
        )}

        {/* Qualifying Prediction */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-sky-300`}>
              <div className="text-sm">{selectionBlockSize === 9 ? 6 : 2} van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Jouw podium voor de <span className='text-sky-300'>kwalificatie</span>
              </h1>
            </div>
            {/* {hasResults ? ( */}
              <div className="bg-white rounded-b-lg pt-2">
                <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                  {qualifyingPrediction.map((prediction, index) => (
                    <DriverCard
                      key={`qual-selected-${prediction.driver.id}`}
                      driver={prediction.driver}
                      type='prediction'
                      weekId={weekId}
                      cardIndex={index}
                    />
                  ))}
                </div>
              </div>
            {/* ) : (
              <EmptyPredictionState />
            )} */}
          </div>

          <MaxPositionDisplay 
            brand={brand}
            title="kwalificatie"
            colorClass="border-b-sky-300 text-sky-300"
            actualPosition={qualifyingResult ? qualifyingResult : undefined}
            predictedPosition={team?.extra?.qualifyingPosition}
            points={qualifyingPoints}
            selectionBlockIndex={selectionBlockSize === 9 ? 7 : 3}
            selectionBlockSize={selectionBlockSize}
          />
        </div>

        {/* Race Prediction */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-amber-400`}>
              <div className="text-sm">{selectionBlockSize === 9 ? 8 : 4} van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Jouw podium voor de <span className='text-amber-400'>race</span>
              </h1>
            </div>
            {/* {hasResults ? ( */}
              <div className="bg-white rounded-b-lg pt-2">
                <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                  {racePrediction.map((prediction: any, index) => (
                    <DriverCard
                      key={`race-selected-${prediction.driver.id}`}
                      driver={prediction.driver}
                      type='prediction'
                      weekId={weekId}
                      cardIndex={index}
                    />
                  ))}
                </div>
              </div>
            {/* ) : (
              <EmptyPredictionState />
            )} */}
          </div>

          <MaxPositionDisplay 
            brand={brand}
            title="race"
            colorClass="border-b-amber-400 text-amber-400"
            actualPosition={raceResult ? raceResult : undefined}
            predictedPosition={team?.extra?.racePosition}
            points={racePoints}
            selectionBlockIndex={selectionBlockSize === 9 ? 9 : 5}
            selectionBlockSize={selectionBlockSize}
          />
        </div>
      </div>
    </>
  )
}

export default Points;